/* eslint-disable max-classes-per-file */
import {
    IExpressionResolver, IExpressionResolution, ResolutionErrorKind, successfulExpressionResolution,
    failedExpressionResolution, IResolutionError, convertStringToExpressionType, IExpressionProperty, ParsingErrorKind
} from 'app/model/expressions/expression-resolution';
import { Owned, Named } from 'app/model/base/named';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ExpressionCategory, ScalarExpression } from './expression';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Parameter } from 'app/model/base/parameter';
import { OwnedSet, LibraryVariable } from 'shell-models-lib';
import { Dataset } from 'app/model/dataset/dataset';
import { getPropertyStack, matchPropertyStackWithSchema, isArraySyntax, buildSubExpressionResolver } from 'app/model/expressions/expression-utils';
import { LinkedService } from 'app/model/linkedservice/linkedservice';

import { stringsAreEqualCaseInsensitive, numberOfLeadingWhitespace } from 'app/shared/utils/string.util';
import { Factory } from '../factory/factory';

/**
 * Base class capable of resolving parameters such as: @pipeline().parameters.password, @dataset().input1, ...
 */
abstract class ParameterResolver implements IExpressionResolver {
    /**
     * Initializes a new instance of the ParameterResolver class.
     * @param _entity The entity owning the field being parameterized.
     * @param _expressionResolversCallback Provides supported expression resolvers used to resolve function arguments.
     */
    public constructor(private _entity: Owned, private readonly _expressionResolversCallback: () => IExpressionResolver[]) {
    }

    protected abstract get conceptModel(): IModel;

    public isOfType(expression: string, ignoreStart: boolean = false): boolean {
        expression = expression ? expression.trim() : '';
        return expression.startsWith(this.getParameterPrefix(ignoreStart));
    }

    public resolve(expression: string, ignoreStart: boolean = false): IExpressionResolution {
        const cleanedExpression = expression ? expression.trim() : '';

        if (this.isOfType(cleanedExpression, ignoreStart)) {
            const trailingSpaceIndex = this._findTrailingSpace(expression);

            if (trailingSpaceIndex >= 0) {
                return failedExpressionResolution(expression, {
                    kind: ResolutionErrorKind.InvalidSyntax,
                    parsingError: {
                        error: ParsingErrorKind.ExtraTrailingSpace,
                        startIndex: trailingSpaceIndex
                    }
                });
            }

            // extract the parameter name
            const parameterNameChain = cleanedExpression.replace(this.getParameterPrefix(ignoreStart), '');
            let propertyNestings: IExpressionProperty[];
            const offset = this.getParameterPrefix(ignoreStart).length + numberOfLeadingWhitespace(expression);

            try {
                propertyNestings = getPropertyStack(parameterNameChain, offset);
            } catch (parsingError) {
                return failedExpressionResolution(expression, {
                    kind: ResolutionErrorKind.InvalidSyntax,
                    parsingError
                });
            }

            if (!propertyNestings || propertyNestings.length <= 0) {
                return failedExpressionResolution(expression, { kind: ResolutionErrorKind.ParameterNotSpecified, startIndex: offset });
            }

            const parameterName = isArraySyntax(propertyNestings[0].property)
                ? propertyNestings[0].property.substring(0, propertyNestings[0].property.indexOf('[')) : propertyNestings[0].property;

            const matchingParameter = this.getConceptParameters(this._entity)?.find((parameter) => stringsAreEqualCaseInsensitive(parameter.name, parameterName));

            if (matchingParameter) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const parameterType = (matchingParameter as any)._type;
                const convertedType = convertStringToExpressionType(parameterType);

                if (!convertedType) {
                    return failedExpressionResolution(expression, {
                        kind: ResolutionErrorKind.UnrecognizedType,
                        details: {
                            source: stringResources.Parameter,
                            expressionType: parameterType
                        }
                    },
                        undefined);
                }

                const propertyExpressionResolution = matchPropertyStackWithSchema(cleanedExpression, propertyNestings, {
                    expressionType: convertedType
                }, offset, buildSubExpressionResolver(this._entity, this._expressionResolversCallback));

                if (propertyExpressionResolution.isValid) {
                    const expressionType = (propertyExpressionResolution.expression as ScalarExpression).returnType;
                    const expressionSubType = (propertyExpressionResolution.expression as ScalarExpression).returnSubType;

                    return successfulExpressionResolution(
                        expression,
                        new ScalarExpression(ExpressionCategory.Parameter, matchingParameter.name, cleanedExpression, `Parameter`, expressionType, expressionSubType),
                        [matchingParameter]);
                } else {
                    return propertyExpressionResolution;
                }
            } else {
                return failedExpressionResolution(expression, {
                    kind: ResolutionErrorKind.ParameterNotFound,
                    details: {
                        parameterName, entity: this._entity,
                        conceptName: this.getRootConceptName()
                    }
                }, undefined, [this.getRootConcept(this._entity)?.named].filter(Boolean));
            }

        } else {
            return failedExpressionResolution(expression, { kind: ResolutionErrorKind.Unrecognized, details: expression }, undefined);
        }
    }

    public checkApplicability(entity: Owned): { isApplicable: boolean, error?: IResolutionError, expression?: string, treatAsWarning?: boolean } {
        const isApplicable = this.isMatchingConcept(entity);
        const resolutionError = isApplicable ? undefined : { kind: ResolutionErrorKind.ParameterInaccessible, details: { target: entity, sourceModel: this.conceptModel } };

        return { isApplicable, error: resolutionError, treatAsWarning: true };
    }

    protected getRootConceptName(): string | undefined {
        return this.getRootConcept(this._entity)?.named.name ?? undefined;
    }

    /**
     * Determines if the entity matches the concept the resolver handles.
     * @param entity The entity to check.
     */
    protected abstract isMatchingConcept(entity: Owned): boolean;

    /**
     * Retrieves the parameters of the entity's concept.
     * @param entity The entity to get the concept parameters for.
     */
    protected abstract getConceptParameters(entity: Owned): OwnedSet<Named>;

    /**
     * Gets the parameter prefix supported.
     * @param ignoreStart True to ignore the leading @ sign (nested level), false to include it in the check (top level).
     */
    protected abstract getParameterPrefix(ignoreStart: boolean): string;

    /**
     * Gets the top level concept of an entity. In case of foreach nested activities, this will return the outermost pipeline.
     * @param entity The entity to get it's top most concept.
     */
    protected getRootConcept(entity: Owned): Owned | undefined {
        let root = entity.concept;

        while (root.owner) {
            root = root.owner;
        }

        return root.concept;
    }

    /**
     * Checks if the character after the last non-whitespace character is a
     * space character, which causes publishing errors.
     * @param expression Expression to check for trailing space.
     * @returns The character position of the trailing space. -1 if there is no
     * trailing space.
     */
    private _findTrailingSpace(expression: string): number {
        const trimmedExpression = expression.trimRight();

        // Whitespace other than space is ok to follow the expression.
        // (e.g. 'pipeline().parameters.tempParam\n ' should succeed.
        if (expression.length !== trimmedExpression.length &&
            expression[trimmedExpression.length] === ' ') {
            return trimmedExpression.length;
        }

        return -1;
    }
}

/**
 * Pipeline parameter resolver implementation class.
 */
export class PipelineParameterResolver extends ParameterResolver {
    protected get conceptModel(): IModel {
        return Pipeline.MODEL;
    }

    protected isMatchingConcept(entity: Owned): boolean {
        return Metamodel.modelOf(this.getRootConcept(entity))?.name === Pipeline.MODEL.name;
    }

    protected getConceptParameters(entity: Owned): OwnedSet<Parameter> | undefined {
        const conceptEntity = this.getRootConcept(entity);
        return conceptEntity ? (conceptEntity as Pipeline).parameters : undefined;
    }

    protected getParameterPrefix(ignoreStart: boolean): string {
        return ignoreStart ? 'pipeline().parameters' : '@pipeline().parameters';
    }
}

/**
 * Data Factory parameter resolver implementation class.
 */
export class DataFactoryParameterResolver extends ParameterResolver {

    constructor(_entity: Owned, _expressionResolversCallback: () => IExpressionResolver[], private readonly _factory: Factory) {
        super(_entity, _expressionResolversCallback);
    }

    protected get conceptModel(): IModel {
        return Factory.MODEL;
    }

    protected getRootConceptName(): string {
        return this._factory.name;
    }

    protected isMatchingConcept(entity: Owned): boolean {
        return Metamodel.modelOf(this.getRootConcept(entity))?.name === Pipeline.MODEL.name;
    }

    protected getConceptParameters(entity: Owned): OwnedSet<Parameter> {
        return this._factory.parameters;
    }

    protected getParameterPrefix(ignoreStart: boolean): string {
        return ignoreStart ? 'pipeline().globalParameters' : '@pipeline().globalParameters';
    }
}

/**
 * Library variable resolver implementation class.
 */
export class LibraryVariableResolver extends ParameterResolver {

    protected get conceptModel(): IModel {
        return Pipeline.MODEL;
    }

    protected isMatchingConcept(entity: Owned): boolean {
        return Metamodel.modelOf(this.getRootConcept(entity))?.name === Pipeline.MODEL.name;
    }

    protected getConceptParameters(entity: Owned): OwnedSet<LibraryVariable> {
        const conceptEntity = this.getRootConcept(entity);
        return conceptEntity ? (conceptEntity as Pipeline).libraryVariables : undefined;
    }

    protected getParameterPrefix(ignoreStart: boolean): string {
        return ignoreStart ? 'pipeline().libraryVariables' : '@pipeline().libraryVariables';
    }
}

/**
 * Dataset parameter resolver implementation class.
 */
export class DatasetParameterResolver extends ParameterResolver {
    protected get conceptModel(): IModel {
        return Dataset.MODEL;
    }

    protected isMatchingConcept(entity: Owned): boolean {
        return entity.concept instanceof Dataset;
    }

    protected getConceptParameters(entity: Owned): OwnedSet<Parameter> {
        return entity.concept ? (entity.concept as Dataset).parameters : undefined;
    }

    protected getParameterPrefix(ignoreStart: boolean): string {
        return ignoreStart ? 'dataset()' : '@dataset()';
    }
}

/**
 * LinkedService parameter resolver implementation class.
 */
export class LinkedServiceParameterResolver extends ParameterResolver {
    protected get conceptModel(): IModel {
        return LinkedService.MODEL;
    }

    protected isMatchingConcept(entity: Owned): boolean {
        return entity.concept instanceof LinkedService;
    }

    protected getConceptParameters(entity: Owned): OwnedSet<Parameter> {
        return entity.concept ? (entity.concept as LinkedService).parameters : undefined;
    }

    protected getParameterPrefix(ignoreStart: boolean): string {
        return ignoreStart ? 'linkedService()' : '@linkedService()';
    }
}
